/**
 * Segment — one cell of a `SegmentedButton`. Renders an optional
 * leading icon, the label, an optional trailing icon, and an optional
 * top-right indicator badge — matching the Figma "Segmented Button -
 * Button - Checkbox - Radiobutton" anatomy (node `3878:27094`).
 *
 * Standalone:
 *   `<Segment label="Foo" selected={true} onClick={...}/>`
 *   Acts as a toggle button; consumer controls the selected state.
 *
 * Inside a `<SegmentedButton mode="radio|checkbox">`:
 *   `<Segment value="phone" label="Phone" leftIcon="call"/>`
 *   The group reads `value`, decides whether this cell is selected,
 *   and fires the right callback on click.
 *
 * Inside a `<SegmentedButton mode="button">`:
 *   `<Segment leftIcon="format_bold" onClick={makeBold}/>`
 *   The group adds the shared chrome (divider, no border on the cell);
 *   each cell's own onClick fires normally.
 *
 * @format
 */

import {isValidElement, useContext, type ReactNode} from 'react';

import {Box} from '@mui/material';

import {styled} from '@mui/material/styles';

import {Icon} from 'ui/components/atoms/Icon';
import {text} from 'ui/theme/typography';
import {
  useSurfaceContext,
  surfaceVar,
  type SurfaceContext as SurfaceContextValue,
} from 'ui/theme/surfaceContext';

import {SegmentedGroupContext, type SegmentedSize, type SegmentedSurface} from './SegmentedButton';

// ── Per-size geometry ──────────────────────────────────────────────────
// `m` (Figma "Normal") matches the source exactly: `p-[16px]` + `gap-[8px]`
// + 16px icon + label.m (14 / 16) → 48px tall naturally. `s` and `l` scale
// our own way using the same control-height ladder we use elsewhere
// (32 / 48 / 64) — these sizes don't exist in Figma yet.

const MIN_HEIGHT: Record<SegmentedSize, string> = {
  s: '32px',
  m: '48px', // Figma "Normal"
  l: '64px',
};

const PAD: Record<SegmentedSize, string> = {
  s: '8px',
  m: '16px', // Figma "Normal" → p-[16px]
  l: '20px',
};

const GAP: Record<SegmentedSize, string> = {
  s: '4px',
  m: '8px', // Figma → gap-[8px]
  l: '8px',
};

const LABEL_ROLE = {
  s: 'label.s',
  m: 'label.m',
  l: 'label.l',
} as const;

const ICON_SIZE: Record<SegmentedSize, number> = {
  s: 14,
  m: 16, // Figma
  l: 20,
};

// ── Props ──────────────────────────────────────────────────────────────

export interface SegmentProps {
  /** Identifier used by `radio` / `checkbox` groups. Optional in `button` mode. */
  value?: string;

  /** Cell label. */
  label?: ReactNode;

  /** Leading icon — Material Symbols name or a React element. */
  leftIcon?: string | ReactNode;

  /** Trailing icon — Material Symbols name or a React element. */
  rightIcon?: string | ReactNode;

  /**
   * Standalone or `button`-mode selected state. Inside a radio /
   * checkbox group this prop is ignored — the group resolves selection
   * from `value`.
   */
  selected?: boolean;

  /** Click handler. In radio / checkbox modes runs AFTER the group logic. */
  onClick?: () => void;

  /** Disable just this cell. The whole group's `disabled` also applies. */
  disabled?: boolean;

  /**
   * Surface-context override for standalone use. When inside a group
   * the group's context wins (it shares one across all cells).
   */
  context?: SurfaceContextValue;

  /** Standalone size override. Ignored when inside a group. */
  size?: SegmentedSize;

  /** Standalone surface override. Ignored when inside a group. */
  surface?: SegmentedSurface;
}

// ── Styled cell ────────────────────────────────────────────────────────

const Cell = styled('button', {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{
  $size: SegmentedSize;
  $surface: SegmentedSurface;
  $selected: boolean;
  $inGroup: boolean;
}>(({theme, $size, $surface, $selected, $inGroup}) => {
  // Active state is always brand primary; idle changes with the
  // `surface` variant so the control reads on whatever surface it sits
  // on. Surface-1/2 tiers exist in BOTH contexts after Path-2, so a
  // SurfaceContextProvider with `data-color-scheme` swap takes care
  // of light-vs-dark — no manual inverse-* lookups here.
  const activeFill = theme.palette.primary.main;
  const activeText = theme.palette.primary.contrastText;
  const idleText = surfaceVar('onSurfaceLight');

  let idleFill: string;
  if ($surface === 'ghost') {
    idleFill = 'transparent';
  } else if ($surface === 'surface-2') {
    idleFill = surfaceVar('surface2');
  } else {
    // surface-1 (Figma source value)
    idleFill = surfaceVar('surface1');
  }

  return {
    // No border anywhere — Figma's container is borderless and the
    // cells inherit zero chrome of their own. Standalone cells also
    // skip the border (rounded radius still applies so they look like
    // pills when used outside a group).
    border: 'none',
    // In a group, only the first and last cells round their outer
    // corners — Row drops `overflow: hidden` so an IndicatorAnchor
    // wrapping any cell can let an indicator stick out without clipping.
    // Standalone cells (outside a group) are fully pill-rounded.
    borderRadius: $inGroup ? 0 : '8px',
    ...($inGroup && {
      '&:first-of-type': {borderRadius: '8px 0 0 8px'},
      '&:last-of-type': {borderRadius: '0 8px 8px 0'},
      '&:first-of-type:last-of-type': {borderRadius: '8px'},
    }),

    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: GAP[$size],
    // All-sides padding per Figma `p-[16px]`. Vertical alignment is
    // handled by flex centering plus `min-height` for the size step.
    padding: PAD[$size],
    minHeight: MIN_HEIGHT[$size],
    boxSizing: 'border-box',
    cursor: 'pointer',
    whiteSpace: 'nowrap',

    background: $selected ? activeFill : idleFill,
    color: $selected ? activeText : idleText,

    ...text(LABEL_ROLE[$size]),
    lineHeight: 1,

    transition: theme.transitions.create(['background', 'color'], {
      duration: theme.transitions.duration.shorter,
    }),

    // No hover state — Figma only gives `cursor-pointer`. Focus ring
    // is the minimal a11y requirement; uses our brand primary at the
    // standard 2px width.
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: -2,
    },
    '&:disabled': {
      cursor: 'not-allowed',
      color: theme.palette.disabledStates.high,
    },
  };
});

// ── Icon slot rendering ────────────────────────────────────────────────

function renderIconSlot(icon: string | ReactNode | undefined, iconPx: number): ReactNode {
  if (!icon) return null;
  if (typeof icon === 'string') return <Icon name={icon} size={iconPx} color='inherit' />;
  if (isValidElement(icon)) return icon;
  return icon;
}

// ── Component ──────────────────────────────────────────────────────────

export function Segment({
  value,
  label,
  leftIcon,
  rightIcon,
  selected: selectedProp,
  onClick,
  disabled: disabledProp,
  context: contextProp,
  size: sizeProp,
  surface: surfaceProp,
}: SegmentProps) {
  const group = useContext(SegmentedGroupContext);

  // Standalone surface-context resolution. When inside a group the
  // group's context already wins, so we only consult the React context
  // for true standalone use.
  const inheritedCtx = useSurfaceContext(contextProp);
  const size = group ? group.size : (sizeProp ?? 'm');
  const surface = group ? group.surface : (surfaceProp ?? 'surface-2');

  // Selection + click resolution.
  const selectedFromGroup = group?.resolveSelected(value);
  const selected = group
    ? // In radio / checkbox modes, group's verdict wins. In button mode,
      // resolveSelected returns false — fall back to the cell's prop.
      group.mode === 'button'
      ? Boolean(selectedProp)
      : selectedFromGroup === true
    : Boolean(selectedProp);

  const cellDisabled = Boolean(disabledProp) || Boolean(group?.disabled);

  const handleClick = () => {
    if (cellDisabled) return;
    if (group) {
      group.handleClick(value, onClick);
    } else {
      onClick?.();
    }
  };

  // ARIA role per group mode (and toggle semantics standalone).
  let role: string | undefined = 'button';
  let ariaChecked: boolean | undefined;
  let ariaPressed: boolean | undefined;

  if (group?.mode === 'radio') {
    role = 'radio';
    ariaChecked = selected;
  } else if (group?.mode === 'checkbox') {
    role = 'checkbox';
    ariaChecked = selected;
  } else {
    // standalone or button-mode: toggle button semantics if selected is
    // meaningful (i.e. consumer is using it), otherwise plain button.
    if (typeof selectedProp === 'boolean') {
      ariaPressed = selected;
    }
  }

  const iconPx = ICON_SIZE[size];

  return (
    <Cell
      type='button'
      role={role}
      aria-checked={ariaChecked}
      aria-pressed={ariaPressed}
      disabled={cellDisabled}
      onClick={handleClick}
      data-color-scheme={inheritedCtx}
      $size={size}
      $surface={surface}
      $selected={selected}
      $inGroup={Boolean(group)}
    >
      {renderIconSlot(leftIcon, iconPx)}
      {label != null && <Box component='span'>{label}</Box>}
      {renderIconSlot(rightIcon, iconPx)}
    </Cell>
  );
}
